/**
 * Form Handlers List
 * Manage public lead-capture endpoints for 3rd-party form builders.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getFormHandlers,
  deleteFormHandler,
  toggleFormHandlerActive,
  getFormHandlerEndpointUrl,
} from "../services/formHandlers.service";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import type { FormHandler } from "../types/formHandler.types";

export default function FormHandlers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const {
    data: handlers,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["form-handlers"],
    queryFn: getFormHandlers,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFormHandler,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-handlers"] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      toggleFormHandlerActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-handlers"] });
    },
  });

  const handleDelete = (e: React.MouseEvent, handler: FormHandler) => {
    e.stopPropagation();
    if (
      window.confirm(
        `Delete form handler "${handler.name}"? All ${handler.submission_count} submission records will also be deleted. This cannot be undone.`,
      )
    ) {
      deleteMutation.mutate(handler.id);
    }
  };

  const handleCopyUrl = (e: React.MouseEvent, handler: FormHandler) => {
    e.stopPropagation();
    const url = getFormHandlerEndpointUrl(handler.id);
    navigator.clipboard.writeText(url);
    setCopiedId(handler.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleToggleActive = (e: React.MouseEvent, handler: FormHandler) => {
    e.stopPropagation();
    toggleMutation.mutate({ id: handler.id, isActive: !handler.is_active });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading form handlers..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load form handlers. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Form Handlers</h1>
          <p className="mt-1 text-sm text-gray-500">
            Public endpoints that capture leads from 3rd-party form builders.
          </p>
        </div>
        <button
          onClick={() => navigate("/form-handlers/new")}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <PlusIcon className="w-4 h-4" />
          New Handler
        </button>
      </div>

      {!handlers || handlers.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <TableCellsIcon className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No form handlers yet
          </h3>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            Create your first handler to start capturing leads from external
            forms.
          </p>
          <button
            onClick={() => navigate("/form-handlers/new")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            New Handler
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Endpoint URL
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submissions
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {handlers.map((handler) => (
                <tr
                  key={handler.id}
                  onClick={() => navigate(`/form-handlers/${handler.id}/edit`)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900 text-sm">
                      {handler.name}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-mono max-w-xs truncate">
                        {getFormHandlerEndpointUrl(handler.id)}
                      </span>
                      <button
                        onClick={(e) => handleCopyUrl(e, handler)}
                        className="p-1 text-gray-400 hover:text-gray-600 shrink-0"
                        title="Copy URL"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                      {copiedId === handler.id && (
                        <span className="text-xs text-green-600">Copied!</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => handleToggleActive(e, handler)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
                        handler.is_active ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                          handler.is_active
                            ? "translate-x-4"
                            : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">
                      {handler.submission_count.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">
                      {new Date(handler.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/form-handlers/${handler.id}/edit`);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/form-handlers/${handler.id}/submissions`);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                        title="View submissions"
                      >
                        <TableCellsIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, handler)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
